/**
 * DoctorSummaryPage - Compilable health summary for medical visits
 * 
 * Displays a clean, readable view of medical information that can be:
 * - Viewed on the phone
 * - Shared securely
 * - Printed
 * - Exported as PDF
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Printer,
  Share2,
  Download,
  FileText,
  User,
  Droplets,
  Pill,
  AlertTriangle,
  Activity,
  Scissors,
  Syringe,
  Stethoscope,
  Calendar,
  CheckCircle2,
  Settings,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemo } from '@/contexts/DemoContext';
import useSharedMedicalData from '@/hooks/useSharedMedicalData';
import API from '@/utils/api';
import { format, parseISO } from 'date-fns';

const THEME = {
  bgGradient: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 30%, #F0FDFA 60%, #F5F3FF 100%)',
};

// Sections that can be included in the summary
const SECTIONS = [
  { id: 'profile', label: 'Basic Information', icon: User },
  { id: 'medications', label: 'Current Medications', icon: Pill },
  { id: 'allergies', label: 'Allergies', icon: AlertTriangle },
  { id: 'conditions', label: 'Medical Conditions', icon: Activity },
  { id: 'surgeries', label: 'Surgeries', icon: Scissors },
  { id: 'vaccinations', label: 'Vaccinations', icon: Syringe },
  { id: 'doctors', label: 'Healthcare Providers', icon: Stethoscope },
  { id: 'appointmentPreps', label: 'Appointment Preparations', icon: Calendar },
];

const DoctorSummaryPage = () => {
  const navigate = useNavigate();
  const printRef = useRef();
  const { isDemoMode, demoData } = useDemo();
  const sharedMedical = useSharedMedicalData();
  
  const [selectedSections, setSelectedSections] = useState(
    SECTIONS.map(s => s.id)
  );
  const [showSettings, setShowSettings] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [appointmentPreps, setAppointmentPreps] = useState([]);

  // Fetch appointment preparations
  useEffect(() => {
    const fetchPreps = async () => {
      if (isDemoMode) return;
      
      try {
        const response = await API.get('/medical/appointment-preps');
        setAppointmentPreps(response.data || []);
      } catch (error) {
        console.error('Failed to fetch appointment preps:', error);
      }
    };
    
    fetchPreps();
  }, [isDemoMode]);

  // Get medical data - use demo data when in demo mode
  const getMedicalData = () => {
    if (isDemoMode && demoData?.medical) {
      return {
        profile: demoData.medical.profile || {},
        medications: demoData.medical.medications || [],
        allergies: demoData.medical.allergies || [],
        conditions: demoData.medical.conditions || [],
        surgeries: demoData.medical.surgeries || [],
        vaccinations: demoData.medical.vaccinations || [],
        doctors: demoData.medical.doctors || [],
        appointmentPreps: [],
      };
    }
    return {
      profile: sharedMedical.profile || {},
      medications: sharedMedical.medications || [],
      allergies: sharedMedical.allergies || [],
      conditions: sharedMedical.conditions || [],
      surgeries: sharedMedical.surgeries || [],
      vaccinations: sharedMedical.vaccinations || [],
      doctors: sharedMedical.doctors || [],
      appointmentPreps: appointmentPreps,
    };
  };

  const data = getMedicalData();

  // Toggle section selection
  const toggleSection = (sectionId) => {
    setSelectedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Handle print
  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  };

  // Handle share
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Medical Summary',
          text: 'My medical summary from Forget Me Not',
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Unable to share');
        }
      }
    } else {
      toast.info('Share not supported on this device');
    }
  };

  // Render section content
  const renderSection = (sectionId) => {
    if (!selectedSections.includes(sectionId)) return null;

    switch (sectionId) {
      case 'profile':
        return (
          <div className="space-y-2">
            {data.profile.name && (
              <div className="flex justify-between">
                <span className="text-slate-600">Name</span>
                <span className="font-medium">{data.profile.name}</span>
              </div>
            )}
            {data.profile.date_of_birth && (
              <div className="flex justify-between">
                <span className="text-slate-600">Date of Birth</span>
                <span className="font-medium">
                  {format(new Date(data.profile.date_of_birth), 'MMMM d, yyyy')}
                </span>
              </div>
            )}
            {data.profile.blood_type && (
              <div className="flex justify-between">
                <span className="text-slate-600">Blood Type</span>
                <Badge variant="outline" className="bg-red-50 text-red-700">
                  <Droplets className="h-3 w-3 mr-1" />
                  {data.profile.blood_type}
                </Badge>
              </div>
            )}
            {data.profile.height && (
              <div className="flex justify-between">
                <span className="text-slate-600">Height</span>
                <span className="font-medium">{data.profile.height}</span>
              </div>
            )}
            {data.profile.weight && (
              <div className="flex justify-between">
                <span className="text-slate-600">Weight</span>
                <span className="font-medium">{data.profile.weight}</span>
              </div>
            )}
          </div>
        );

      case 'medications':
        if (data.medications.length === 0) {
          return <p className="text-slate-500 text-sm">No medications recorded</p>;
        }
        return (
          <div className="space-y-2">
            {data.medications.map((med, idx) => (
              <div key={med.id || idx} className="p-2 bg-purple-50 rounded-lg">
                <div className="font-medium text-purple-900">{med.name}</div>
                {med.dosage && (
                  <div className="text-sm text-purple-700">{med.dosage}</div>
                )}
                {med.frequency && (
                  <div className="text-sm text-purple-600">{med.frequency}</div>
                )}
                {med.reason && (
                  <div className="text-xs text-purple-500 mt-1">For: {med.reason}</div>
                )}
              </div>
            ))}
          </div>
        );

      case 'allergies':
        if (data.allergies.length === 0) {
          return <p className="text-slate-500 text-sm">No known allergies</p>;
        }
        return (
          <div className="space-y-2">
            {data.allergies.map((allergy, idx) => (
              <div key={allergy.id || idx} className="p-2 bg-red-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-red-900">{allergy.name}</span>
                  {allergy.severity && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        allergy.severity === 'life-threatening' || allergy.severity === 'severe'
                          ? 'bg-red-100 text-red-700 border-red-300'
                          : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {allergy.severity}
                    </Badge>
                  )}
                </div>
                {allergy.reaction && (
                  <div className="text-sm text-red-700 mt-1">
                    Reaction: {allergy.reaction}
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'conditions':
        if (data.conditions.length === 0) {
          return <p className="text-slate-500 text-sm">No conditions recorded</p>;
        }
        return (
          <div className="space-y-2">
            {data.conditions.map((condition, idx) => (
              <div key={condition.id || idx} className="p-2 bg-amber-50 rounded-lg">
                <div className="font-medium text-amber-900">{condition.name}</div>
                {condition.diagnosed_date && (
                  <div className="text-sm text-amber-700">
                    Diagnosed: {format(new Date(condition.diagnosed_date), 'MMM yyyy')}
                  </div>
                )}
                {condition.status && (
                  <Badge variant="outline" className="mt-1 text-xs">
                    {condition.status}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        );

      case 'surgeries':
        if (data.surgeries.length === 0) {
          return <p className="text-slate-500 text-sm">No surgeries recorded</p>;
        }
        return (
          <div className="space-y-2">
            {data.surgeries.map((surgery, idx) => (
              <div key={surgery.id || idx} className="p-2 bg-slate-50 rounded-lg">
                <div className="font-medium text-slate-900">{surgery.name}</div>
                {surgery.date && (
                  <div className="text-sm text-slate-600">
                    {format(new Date(surgery.date), 'MMMM d, yyyy')}
                  </div>
                )}
                {surgery.hospital && (
                  <div className="text-sm text-slate-500">{surgery.hospital}</div>
                )}
              </div>
            ))}
          </div>
        );

      case 'vaccinations':
        if (data.vaccinations.length === 0) {
          return <p className="text-slate-500 text-sm">No vaccinations recorded</p>;
        }
        return (
          <div className="space-y-2">
            {data.vaccinations.map((vax, idx) => (
              <div key={vax.id || idx} className="flex items-center justify-between p-2 bg-teal-50 rounded-lg">
                <span className="font-medium text-teal-900">{vax.name}</span>
                {vax.date && (
                  <span className="text-sm text-teal-700">
                    {format(new Date(vax.date), 'MMM yyyy')}
                  </span>
                )}
              </div>
            ))}
          </div>
        );

      case 'doctors':
        if (data.doctors.length === 0) {
          return <p className="text-slate-500 text-sm">No providers recorded</p>;
        }
        return (
          <div className="space-y-2">
            {data.doctors.map((doc, idx) => (
              <div key={doc.id || idx} className="p-2 bg-blue-50 rounded-lg">
                <div className="font-medium text-blue-900">{doc.name}</div>
                {doc.specialty && (
                  <div className="text-sm text-blue-700">{doc.specialty}</div>
                )}
                {doc.phone && (
                  <div className="text-sm text-blue-600">{doc.phone}</div>
                )}
              </div>
            ))}
          </div>
        );

      case 'appointmentPreps':
        if (data.appointmentPreps.length === 0) {
          return <p className="text-slate-500 text-sm">No appointment preparations</p>;
        }
        return (
          <div className="space-y-3">
            {data.appointmentPreps.map((prep, idx) => (
              <div key={prep.id || idx} className="p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-emerald-900">
                    {prep.doctorName || 'Appointment'}
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${
                      prep.status === 'completed' 
                        ? 'bg-slate-100 text-slate-600' 
                        : prep.status === 'ready'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {prep.status}
                  </Badge>
                </div>
                {prep.appointmentDate && (
                  <div className="text-sm text-emerald-700 mb-2">
                    {format(parseISO(prep.appointmentDate), 'EEEE, MMMM d, yyyy')}
                    {prep.appointmentTime && ` at ${prep.appointmentTime}`}
                  </div>
                )}
                {prep.reason && (
                  <div className="text-sm text-emerald-600 mb-2">
                    <strong>Reason:</strong> {prep.reason}
                  </div>
                )}
                {prep.symptoms && prep.symptoms.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs font-medium text-emerald-700 mb-1">Symptoms:</div>
                    <ul className="text-sm text-emerald-600 list-disc list-inside">
                      {prep.symptoms.map((s, i) => (
                        <li key={s.id || i}>{s.text}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {prep.questions && prep.questions.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs font-medium text-emerald-700 mb-1">Questions:</div>
                    <ul className="text-sm text-emerald-600 space-y-1">
                      {prep.questions.map((q, i) => (
                        <li key={q.id || i} className="flex items-start gap-2">
                          {q.answered ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          ) : (
                            <span className="h-4 w-4 rounded-full border border-emerald-400 mt-0.5 flex-shrink-0" />
                          )}
                          <span className={q.answered ? 'line-through text-slate-500' : ''}>
                            {q.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(prep.followUpNotes || prep.nextSteps) && (
                  <div className="mt-2 pt-2 border-t border-emerald-200">
                    {prep.followUpNotes && (
                      <div className="text-sm text-emerald-600">
                        <strong>Follow-up:</strong> {prep.followUpNotes}
                      </div>
                    )}
                    {prep.nextSteps && (
                      <div className="text-sm text-emerald-600">
                        <strong>Next steps:</strong> {prep.nextSteps}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="doctor-summary-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-emerald-100 print:hidden">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/medical')}
                className="p-2 -ml-2"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-slate-800">
                  Doctor Summary
                </h1>
                <p className="text-xs text-slate-500">
                  Your health information at a glance
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
              >
                <Share2 className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                onClick={handlePrint}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Printable Content */}
      <main className="max-w-3xl mx-auto px-4 py-6" ref={printRef}>
        {/* Print Header */}
        <div className="hidden print:block mb-6 pb-4 border-b-2 border-emerald-500">
          <h1 className="text-2xl font-bold text-slate-800">Medical Summary</h1>
          <p className="text-slate-600">Generated: {format(new Date(), 'MMMM d, yyyy')}</p>
        </div>

        <div className="space-y-4">
          {SECTIONS.map((section) => {
            if (!selectedSections.includes(section.id)) return null;
            
            const SectionIcon = section.icon;
            return (
              <Card key={section.id} className="shadow-sm print:shadow-none print:break-inside-avoid">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <SectionIcon className="h-4 w-4 text-emerald-600" />
                    {section.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {renderSection(section.id)}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Print Footer */}
        <div className="hidden print:block mt-8 pt-4 border-t text-center text-sm text-slate-500">
          <p>Generated by Forget Me Not</p>
        </div>
      </main>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-emerald-600" />
              Select Information to Include
            </DialogTitle>
            <DialogDescription>
              Choose which sections appear in your doctor summary
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {SECTIONS.map((section) => (
              <label 
                key={section.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <Checkbox
                  checked={selectedSections.includes(section.id)}
                  onCheckedChange={() => toggleSection(section.id)}
                />
                <section.icon className="h-4 w-4 text-slate-500" />
                <span className="text-sm">{section.label}</span>
              </label>
            ))}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowSettings(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default DoctorSummaryPage;
